import React from "react";
import {
  Flex,
  Box,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  Button,
  useColorModeValue,
  Badge,
  HStack,
  VStack,
  List,
  ListItem,
  ListIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import {
  FaUser,
  FaCheckCircle,
  FaArrowRight,
  FaAddressCard,
  FaSitemap,
  FaRegCommentAlt,
  FaEnvelope,
  FaLock,
  FaHome,
} from "react-icons/fa";

const NormalUserDoc = () => {
  const cardBg = useColorModeValue("white", "navy.800");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const calloutBg = useColorModeValue("slate.50", "navy.900");

  const sections = [
    {
      title: "1. Account Activation & Sign In",
      icon: FaEnvelope,
      color: "teal",
      points: [
        "When you are invited, you will receive an email containing an account registration link.",
        "Click the link and fill out your First Name, Last Name, Professional Title, Institution, and choose a password.",
        "Sign in using your email and password on the Sign In page.",
        "If you forget your password, click 'Forgot Password' on the login screen to receive a password reset link.",
      ],
    },
    {
      title: "2. Exploring Individuals",
      icon: FaAddressCard,
      color: "blue",
      points: [
        "Access the Individuals section from the left navigation sidebar to view all patient records.",
        "Search and filter records by Individual Code, Consanguinity Degree, Complementation Group, or Sex.",
        "Click any row to open the full profile: general demographics, dermatological & ocular signs, neurological history, and genetic variants.",
        "As a User, your access is view-only to safeguard clinical data integrity.",
      ],
    },
    {
      title: "3. Families & Lineages",
      icon: FaSitemap,
      color: "purple",
      points: [
        "Navigate to Families to review family lineages and parental consanguinity patterns.",
        "Click on a Family Code to inspect all linked family members and their respective clinical status.",
        "Evaluate hereditary patterns and transmission dynamics across documented branches.",
      ],
    },
    {
      title: "4. Scientific Community Forum",
      icon: FaRegCommentAlt,
      color: "teal",
      points: [
        "Collaborate with other authorized researchers and clinicians in the community forum.",
        "Create new discussion threads to ask questions or share insights on patient care and genetics.",
        "Comment on active threads to exchange knowledge with fellow experts.",
        "Ensure patient confidentiality: do not share direct identifying personal information.",
      ],
    },
  ];

  return (
    <Flex direction="column" pt={{ base: "62px", md: "75px", xl: "88px" }} pb="60px" px={{ base: "16px", md: "36px" }}>
      <Box maxW="1000px" mx="auto" w="100%">
        {/* Breadcrumb Navigation */}
        <Breadcrumb mb="20px" fontSize="sm" color={subtitleColor}>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/txd/doc">
              <HStack spacing="4px">
                <Icon as={FaHome} />
                <span>User Guide</span>
              </HStack>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="teal.500" fontWeight="600">
              User (Collaborator) Manual
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Page Header */}
        <Box mb="32px">
          <Badge colorScheme="teal" variant="solid" px="10px" py="3px" borderRadius="full" fontSize="xs" fontWeight="700" mb="8px">
            User Role Manual
          </Badge>
          <Heading size="xl" color={textColor} fontWeight="800" mb="8px">
            Collaborator &amp; Clinical User Guide
          </Heading>
          <Text fontSize="md" color={subtitleColor} lineHeight="tall">
            Step-by-step instructions for authorized collaborators using the Tunisian XP Database to consult clinical datasets and engage with the scientific community.
          </Text>
        </Box>

        {/* Guide Sections */}
        <VStack spacing="24px" align="stretch">
          {sections.map((sec, idx) => (
            <Box
              key={idx}
              bg={cardBg}
              p="26px"
              borderRadius="20px"
              border="1px solid"
              borderColor={cardBorder}
              boxShadow="0 4px 16px rgba(0,0,0,0.03)"
            >
              <HStack spacing="12px" mb="16px">
                <Box p="10px" borderRadius="12px" bg={`${sec.color}.50`} color={`${sec.color}.600`} _dark={{ bg: "whiteAlpha.100", color: `${sec.color}.300` }}>
                  <Icon as={sec.icon} boxSize="20px" />
                </Box>
                <Heading size="md" color={textColor}>
                  {sec.title}
                </Heading>
              </HStack>
              <List spacing={3} fontSize="sm" color={subtitleColor}>
                {sec.points.map((pt, pIdx) => (
                  <ListItem key={pIdx} display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="teal.500" mt="3px" />
                    <span>{pt}</span>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </VStack>

        {/* Return Button */}
        <Box mt="32px" textAlign="center">
          <Button as={Link} to="/txd/doc" colorScheme="teal" variant="outline" rightIcon={<FaArrowRight />}>
            Back to Documentation Hub
          </Button>
        </Box>
      </Box>
    </Flex>
  );
};

export default NormalUserDoc;